import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BLOG_POSTS, formatDate, type BlogPost } from '@/content/blog';
import { IconArrowRight } from '@/components/home/icons';

/**
 * Blog index — a featured article plus a grid of the rest. Adapted from
 * the Stitch "Blog" layout into the dark night/beam theme.
 */
export function Blog() {
  useDocumentTitle('Blog');
  const [featured, ...rest] = BLOG_POSTS;

  return (
    <section className="container-wide pb-24 pt-12 sm:pt-16">
      <header className="mb-10 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
          Journal
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-night-50 sm:text-4xl">
          Notes on reading the Quran
        </h1>
        <p className="mt-3 text-night-200">
          Short, practical writing on building a habit, studying with tafsir,
          memorizing, and reading in translation.
        </p>
      </header>

      {featured && <FeaturedCard post={featured} />}

      {rest.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {rest.map((post) => (
            <ArticleCard key={post.slug} post={post} />
          ))}
        </div>
      )}
    </section>
  );
}

function FeaturedCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group grid overflow-hidden rounded-2xl border border-night-500/40 bg-night-700/60 transition-all duration-200 hover:border-beam/60 md:grid-cols-2"
    >
      <div className="relative flex min-h-[220px] items-center justify-center overflow-hidden border-b border-night-500/30 bg-gradient-to-br from-night-700 to-night-800 md:border-b-0 md:border-r">
        <div
          aria-hidden="true"
          className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-beam/15 blur-3xl"
        />
        <span className="relative font-display text-xl font-extrabold uppercase tracking-[0.18em] text-beam/80">
          {post.category}
        </span>
      </div>
      <div className="flex flex-col p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-beam">
          Featured
        </p>
        <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-night-50">
          {post.title}
        </h2>
        <p className="mt-3 leading-relaxed text-night-200">{post.excerpt}</p>
        <div className="mt-auto flex items-center gap-3 pt-6 text-xs text-night-300">
          <span>{formatDate(post.date)}</span>
          <span>·</span>
          <span>{post.readingMinutes} min read</span>
        </div>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-beam">
          Read article
          <IconArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function ArticleCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col rounded-2xl border border-night-500/40 bg-night-700/60 p-7 transition-all duration-200 hover:-translate-y-0.5 hover:border-beam/60"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-beam">
        {post.category}
      </p>
      <h3 className="mt-3 font-display text-lg font-bold text-night-50">
        {post.title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-night-200">
        {post.excerpt}
      </p>
      <div className="mt-5 flex items-center gap-3 text-xs text-night-300">
        <span>{formatDate(post.date)}</span>
        <span>·</span>
        <span>{post.readingMinutes} min</span>
      </div>
    </Link>
  );
}
